import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  cancelAddon,
  cancelSubscription,
  changePlan,
  getActiveSubscription,
  getFrozenResources,
  getPaymentStatus,
  getPlanList,
  getPublicPlanList,
  getSubscriptionForDisplay,
  getUsageAlertSettings,
  previewPlanChange,
  reactivateSubscription,
  syncShopifyBilling,
  unfreezeChatbot,
  updateUsageAlertSettings,
} from '../requests/subscription.requests';

/**
 * For Shopify merchants: ask the API to pull the current subscription from
 * Shopify and sync the plan, then refetch everything plan-derived. Recovery
 * for missed app_subscriptions/update webhooks — without it, a plan bought
 * on the Managed Pricing page can stay invisible (Starter banner, "Upgrade"
 * button) until Shopify happens to resend a webhook. Fires once per mount.
 */
export const useSyncShopifyBilling = (isShopifyMerchant: boolean) => {
  const queryClient = useQueryClient();
  const firedRef = useRef(false);

  useEffect(() => {
    if (!isShopifyMerchant || firedRef.current) return;
    firedRef.current = true;
    syncShopifyBilling()
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['active-subscription'] });
        queryClient.invalidateQueries({ queryKey: ['subscription-display'] });
        queryClient.invalidateQueries({ queryKey: ['feature-usage-status'] });
        queryClient.invalidateQueries({ queryKey: ['user'] });
      })
      .catch(() => {
        // Best-effort: the webhook path remains the primary sync mechanism.
      });
  }, [isShopifyMerchant, queryClient]);
};

export const useGetPlanList = (
  initialData?: import('@/types').IPlan[],
  initialDataUpdatedAt?: number
) =>
  useQuery({
    // The public HTML uses a compact catalog. Never seed account billing with it.
    queryKey: initialData ? ['plans', 'public'] : ['plans'],
    initialData,
    initialDataUpdatedAt,
    // Model names and entitlements come from the live catalog, including on
    // long-lived pricing tabs. Share the refresh across all pricing surfaces.
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const res = await (initialData ? getPublicPlanList() : getPlanList());
      return res.data;
    },
    retry: false,
  });

export const useGetActiveSubscription = () =>
  useQuery({
    queryKey: ['active-subscription'],
    queryFn: async () => {
      const res = await getActiveSubscription();
      return res.data.subscription; // Extract subscription from response data
    },
    retry: false,
  });

export const useGetSubscriptionForDisplay = () =>
  useQuery({
    queryKey: ['subscription-display'],
    queryFn: async () => {
      const res = await getSubscriptionForDisplay();
      return res.data.subscription;
    },
    retry: false,
  });

export const useReactivateSubscription = () =>
  useMutation({
    mutationFn: reactivateSubscription,
  });

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      subscriptionId,
      cancelReason,
    }: {
      subscriptionId: string;
      cancelReason?: string;
    }) => cancelSubscription(subscriptionId, cancelReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-subscription'] });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['feature-usage-status'] });
    },
  });
};

export const useCancelAddon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ addonId, quantity }: { addonId: string; quantity?: number }) =>
      cancelAddon(addonId, quantity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-subscription'] });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['feature-usage-status'] });
    },
  });
};

// Phase 8: Frozen Resources Query
export const useGetFrozenResources = () =>
  useQuery({
    queryKey: ['frozen-resources'],
    queryFn: async () => {
      const res = await getFrozenResources();
      return res.data;
    },
    retry: false,
    refetchInterval: 60000, // Refetch every minute to update countdown
  });

// Phase 8: Payment Status Query
export const useGetPaymentStatus = () =>
  useQuery({
    queryKey: ['payment-status'],
    queryFn: async () => {
      const res = await getPaymentStatus();
      return res.data;
    },
    retry: false,
    refetchInterval: 300000, // Refetch every 5 minutes
  });

export const useGetUsageAlertSettings = () =>
  useQuery({
    queryKey: ['usage-alert-settings'],
    queryFn: async () => {
      const res = await getUsageAlertSettings();
      return res.data.settings;
    },
    retry: false,
  });

export const useUpdateUsageAlertSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: any) => updateUsageAlertSettings(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usage-alert-settings'] });
    },
  });
};

// NEW: Plan Change Mutations (Preserves Add-ons)
export const usePreviewPlanChange = () => {
  return useMutation({
    mutationFn: (data: {
      subscriptionId: string;
      newPlanId: string;
      billingCycle?: 'monthly' | 'yearly';
    }) => previewPlanChange(data),
  });
};

export const useChangePlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      subscriptionId: string;
      newPlanId: string;
      immediate?: boolean;
      billingCycle?: 'monthly' | 'yearly';
    }) => changePlan(data),
    onSuccess: () => {
      // Invalidate all relevant queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['active-subscription'] });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['feature-usage-status'] });
      queryClient.invalidateQueries({ queryKey: ['plans'] });
    },
  });
};

// Unfreeze a single chatbot from the dashboard
export const useUnfreezeChatbot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatbotId: string) => unfreezeChatbot(chatbotId),
    onSuccess: () => {
      // Invalidate bot list queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['bots'] });
      queryClient.invalidateQueries({ queryKey: ['frozen-resources'] });
      queryClient.invalidateQueries({ queryKey: ['feature-usage-status'] });
    },
  });
};
